import React, { useState } from "react";
import { Alert, Button, Dialog, TextInput } from "@narmi/design_system";
import {
  createBankingToken,
  destroyBankingToken,
  updateBankingToken,
} from "./api";
import { DeserializedBankingToken, DialogProps } from "./types";

const TokenWriteForm = ({
  nickname,
  ipAllowList,
  onChangeNickname,
  onChangeIpAllowList,
}: {
  nickname: string;
  ipAllowList: string;
  onChangeNickname: (val: string) => void;
  onChangeIpAllowList: (val: string) => void;
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-around",
      gap: "12px",
      marginTop: "12px",
    }}
  >
    <TextInput
      label="Nickname"
      value={nickname}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        onChangeNickname(e.target.value)
      }
    />
    <TextInput
      label="IP allowlist"
      value={ipAllowList}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        onChangeIpAllowList(e.target.value)
      }
    />
    <span>Comma-separated IPv4 or IPv6.</span>
  </div>
);

export const TokenDialogSubmitFooter = ({
  buttonText,
  onCancel,
  onConfirm,
}: {
  buttonText: string;
  onCancel: () => void;
  onConfirm: () => void;
}) => (
  <div style={{ display: "flex", justifyContent: "flex-end" }}>
    <Button style={{ paddingRight: "10px" }} kind="plain" onClick={onCancel}>
      Cancel
    </Button>
    <Button onClick={onConfirm}>{buttonText}</Button>
  </div>
);

export const TokenRevokeDialog = ({
  isOpen,
  onUserDismiss,
  onConfirm,
  tokenForRevoke,
}: DialogProps & { tokenForRevoke: DeserializedBankingToken | null }) => {
  if (!tokenForRevoke) {
    return null;
  }

  return (
    <Dialog
      title="Revoke API token"
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      footer={
        <TokenDialogSubmitFooter
          buttonText="Revoke token"
          onCancel={onUserDismiss}
          onConfirm={() =>
            destroyBankingToken(tokenForRevoke.uuid).then(onConfirm)
          }
        />
      }
    >
      <p>
        Are you sure you want to revoke this API token? This will expire the
        token instantly, rendering it unusable.
      </p>
    </Dialog>
  );
};
export const useTokenForm = ({
  onSubmit,
  updateToken,
}: {
  onSubmit: (data: {
    nickname: string;
    ip_allowlist: string[];
  }) => Promise<void> | void;
  updateToken?: DeserializedBankingToken;
}) => {
  const initialNickName = updateToken?.nickname || "";
  const initialIpAllowlist = updateToken?.ipAllowlist.join(", ") || "";

  const [nickname, setNickName] = useState(initialNickName);
  const [ipAllowList, setIpAllowList] = useState(initialIpAllowlist);
  const [alertEl, setAlertEl] = useState<React.ReactNode>(null);

  const validate = () => {
    if (![nickname, ipAllowList].every(Boolean)) {
      setAlertEl(
        <Alert isActive kind="error">
          All fields are required.
        </Alert>,
      );
      return false;
    }
    return true;
  };

  const handleConfirm = async () => {
    setAlertEl(null);
    if (!validate()) return;
    const data = {
      nickname,
      ip_allowlist: ipAllowList.split(",").map((ip) => ip.trim()),
    };
    await onSubmit(data);
  };

  const tokenForm = (
    <TokenWriteForm
      nickname={nickname}
      ipAllowList={ipAllowList}
      onChangeNickname={setNickName}
      onChangeIpAllowList={setIpAllowList}
    />
  );

  return {
    tokenForm,
    alertEl,
    handleConfirm,
  };
};

export const TokenCreateDialog = ({
  isOpen,
  onUserDismiss,
  onConfirm,
}: DialogProps) => {
  const { tokenForm, alertEl, handleConfirm } = useTokenForm({
    onSubmit: (data) => createBankingToken(data).then(onConfirm),
  });

  return (
    <Dialog
      title="Create an API token"
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      footer={
        <TokenDialogSubmitFooter
          buttonText="Create token"
          onCancel={onUserDismiss}
          onConfirm={handleConfirm}
        />
      }
      notification={alertEl}
    >
      {tokenForm}
    </Dialog>
  );
};

export const TokenUpdateDialog = ({
  isOpen,
  onUserDismiss,
  onConfirm,
  tokenForUpdate,
}: DialogProps & { tokenForUpdate: DeserializedBankingToken | null }) => {
  if (!tokenForUpdate) {
    return null;
  }
  const { tokenForm, alertEl, handleConfirm } = useTokenForm({
    onSubmit: (data) =>
      updateBankingToken(tokenForUpdate.uuid, data).then(onConfirm),
    updateToken: tokenForUpdate,
  });

  return (
    <Dialog
      title="Edit API token"
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      footer={
        <TokenDialogSubmitFooter
          buttonText="Update token"
          onCancel={onUserDismiss}
          onConfirm={handleConfirm}
        />
      }
      notification={alertEl}
    >
      {tokenForm}
    </Dialog>
  );
};
